import React, { useState } from 'react';
import { StepProps } from '../MultiStepModal';

type PriceMode = 'fixed' | 'estimated' | 'hourly';

export default function JobPricingStep({ data, onDataChange }: StepProps) {
  const [mode, setMode] = useState<PriceMode>(data.pricingMode || 'fixed');

  const setMode_ = (m: PriceMode) => {
    setMode(m);
    onDataChange({ pricingMode: m, estimatedRevenue: 0, estimatedCost: 0, hourlyRate: 0 });
  };

  return (
    <div className="step-form">
      <div className="step-header">
        <h3 className="step-form-title">Pricing</h3>
        <p className="step-form-subtitle">Set how this job will be priced. You can update this later.</p>
      </div>

      {/* Mode picker */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {([
          { v: 'fixed',     label: 'Fixed price',     icon: '💷' },
          { v: 'estimated', label: 'Estimated',        icon: '≈' },
          { v: 'hourly',    label: 'Hourly rate',      icon: '⏱' },
        ] as { v: PriceMode; label: string; icon: string }[]).map(({ v, label, icon }) => (
          <button key={v} type="button" onClick={() => setMode_(v)}
            className={`flex flex-col items-center rounded-xl border px-3 py-3 text-center transition-all ${
              mode === v ? 'border-[#1B1B1B] bg-[#F8F8F8] ring-1 ring-[#1B1B1B]' : 'border-[#E6E6E4] bg-white hover:border-[#CDCDCD]'
            }`}>
            <span className="text-xl mb-1">{icon}</span>
            <span className="text-[11px] font-medium text-[#374151]">{label}</span>
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {mode === 'fixed' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-medium text-[#374151] mb-1">Revenue (£)</label>
              <input type="number" min={0} step={0.01} placeholder="0.00"
                value={data.estimatedRevenue || ''}
                onChange={e => onDataChange({ estimatedRevenue: parseFloat(e.target.value) || 0 })}
                className="w-full rounded-lg border border-[#E6E6E4] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1B1B1B]" />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-[#374151] mb-1">Cost (£)</label>
              <input type="number" min={0} step={0.01} placeholder="0.00"
                value={data.estimatedCost || ''}
                onChange={e => onDataChange({ estimatedCost: parseFloat(e.target.value) || 0 })}
                className="w-full rounded-lg border border-[#E6E6E4] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1B1B1B]" />
            </div>
          </div>
        )}

        {mode === 'estimated' && (
          <>
            <p className="text-[11px] text-[#6B7280]">Estimated totals — exact pricing confirmed after job completion.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-medium text-[#374151] mb-1">Est. revenue (£)</label>
                <input type="number" min={0} step={0.01} placeholder="0.00"
                  value={data.estimatedRevenue || ''}
                  onChange={e => onDataChange({ estimatedRevenue: parseFloat(e.target.value) || 0 })}
                  className="w-full rounded-lg border border-[#E6E6E4] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1B1B1B]" />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-[#374151] mb-1">Est. cost (£)</label>
                <input type="number" min={0} step={0.01} placeholder="0.00"
                  value={data.estimatedCost || ''}
                  onChange={e => onDataChange({ estimatedCost: parseFloat(e.target.value) || 0 })}
                  className="w-full rounded-lg border border-[#E6E6E4] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1B1B1B]" />
              </div>
            </div>
          </>
        )}

        {mode === 'hourly' && (
          <>
            <div>
              <label className="block text-[11px] font-medium text-[#374151] mb-1">Hourly rate (£/hr)</label>
              <input type="number" min={0} step={0.01} placeholder="0.00"
                value={data.hourlyRate || ''}
                onChange={e => {
                  const rate = parseFloat(e.target.value) || 0;
                  const hrs = (data.estimatedDuration || 60) / 60;
                  onDataChange({ hourlyRate: rate, estimatedRevenue: Math.round(rate * hrs * 100) / 100 });
                }}
                className="w-full rounded-lg border border-[#E6E6E4] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1B1B1B]" />
            </div>
            {data.hourlyRate > 0 && data.estimatedDuration > 0 && (
              <p className="text-[12px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                Estimated total: £{((data.hourlyRate * data.estimatedDuration) / 60).toFixed(2)} ({data.estimatedDuration} mins at £{data.hourlyRate}/hr)
              </p>
            )}
          </>
        )}

        <div>
          <label className="block text-[11px] font-medium text-[#374151] mb-1">Priority</label>
          <div className="flex gap-2">
            {(['LOW','MEDIUM','HIGH','URGENT'] as const).map(p => (
              <button key={p} type="button" onClick={() => onDataChange({ priority: p })}
                className={`flex-1 rounded-lg border px-2 py-1.5 text-[11px] font-medium transition-all ${
                  data.priority === p ? 'border-[#1B1B1B] bg-[#1B1B1B] text-white' : 'border-[#E6E6E4] bg-white text-[#374151] hover:border-[#CDCDCD]'
                }`}>{p}</button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
